import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';

import { Store } from '@ngrx/store';

import { IManageProcessItems } from '@components/products/item-processes/manage-items-processes/manage-items-processes.component';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SafeAny } from '../../../types/safe-any';

import { selectItemProcessCategories } from './item-processes.selectors';

export interface ItemProcessGroup {
  name: string;
  items: IItemProcess[];
  categoryKey: string;
  color?: string;
  hasBasePrice?: boolean;
  basePrice?: number;
  pagination?: {
    totalCount: number; // Will be 0 or inaccurate due to API limitation
    pageIndex: number;
    pageSize: number;
  };
  sort?: {
    active: string;
    direction: SortDirection; // 'asc' | 'desc' | ''
  };
  loading?: boolean;
}

export interface ItemProcessCategory {
  key: string;
  value: string;
  basePrice?: number | null;
}

export interface IItemProcess {
  id: number;
  itemId: string;
  itemType: string;
  description: string;
  lbPer1000ft: number | null;
  pricePerLb: number | null;
  pricePerUnit: number | null;
  specificGravity: number | null;
  constant1: number | null;
  constant2: number | null;
  dateUpdated: string;
  dateCreated: string;
}

export interface IAddItemProcessPayload {
  itemId: string;
  itemType: string;
  description: string;
  lbPer1000ft?: number | null;
  pricePerLb?: number | null;
  pricePerUnit?: number | null;
  specificGravity?: number | null;
  constant1?: number | null;
  constant2?: number | null;
}

const ITEM_PROCESS_CATEGORIES_API_PREFIX = 'item-processes';

@Injectable({ providedIn: 'root' })
export class ItemProcessesService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);

  constructor() {}

  getItemProcesses(payload: {
    q?: string;
    limit?: number;
    offset?: number;
    sort?: string;
  }): Observable<ItemProcessGroup[]> {
    const { q: searchTerm, limit = 5, offset = 1, sort } = payload;
    const pageIndex = offset > 1 ? Math.floor((offset - 1) / limit) : 0;
    const sortActive = sort ? sort.replace(/^-/, '') : '';
    const sortDirection = sort ? (sort.startsWith('-') ? 'desc' : 'asc') : '';

    // Get categories from store
    return this.store.select(selectItemProcessCategories).pipe(
      switchMap((categories) => {
        if (!categories || categories.length === 0) {
          return of([]);
        }

        // Create requests for each category to load its items
        const categoryItemRequests: Observable<ItemProcessGroup>[] = categories.map(
          (category: ItemProcessCategory) =>
            this.getItemProcessesForCategory(category.key, searchTerm, limit, offset, sort).pipe(
              map((response) =>
                this.mapCategoryResponseToGroup(
                  category,
                  response,
                  limit,
                  pageIndex,
                  sortActive,
                  sortDirection,
                ),
              ),
            ),
        );

        return forkJoin<ItemProcessGroup[]>(categoryItemRequests);
      }),
    );
  }

  private getItemProcessesForCategory(
    category: string,
    q?: string,
    limit = 5,
    offset = 1,
    sort?: string,
  ): Observable<HttpResponse<IItemProcess[]>> {
    let params = new HttpParams()
      .set('category', category)
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (q) {
      params = params.set('q', q);
    }
    if (sort) {
      params = params.set('sort', sort);
    }

    return this.httpClient.get<IItemProcess[]>(
      `${environment.apiUrl}/${ITEM_PROCESS_CATEGORIES_API_PREFIX}`,
      {
        params: params,
        observe: 'response', // This tells HttpClient to give us the full response, including headers
      },
    );
  }

  /**
   * Gets the item process categories from the API.
   * @returns {Observable<ItemProcessCategory[]>}
   */
  getItemProcessCategories(): Observable<ItemProcessCategory[]> {
    return this.httpClient.get<ItemProcessCategory[]>(
      `${environment.apiUrl}/${ITEM_PROCESS_CATEGORIES_API_PREFIX}/categories`,
    );
  }

  /**
   * Adds a new item process via HTTP POST.
   * Transforms the form data (IManageProcessItems) into the API payload structure.
   * @param item The item data from the form.
   * @returns Observable<IItemProcess> The created item process as returned by the API.
   */
  addItemProcess(item: IManageProcessItems): Observable<IItemProcess> {
    const payload: IAddItemProcessPayload = {
      itemId: item.itemId || '', // Ensure itemId is provided, default to empty string if undefined
      itemType: item.category,
      description: item.description,
      // Map specificGravity/lbPer1000ft based on category
      lbPer1000ft: item.lbPer1000ft,
      specificGravity: item.category !== 'Copper' ? +item.specificGravity : null,
      // Map price to appropriate field based on category
      pricePerLb: item.pricePerLb,
      pricePerUnit: item.pricePerUnit,
      constant1: item.constant1 ?? null, // Use nullish coalescing for optional fields
      constant2: item.constant2 ?? null,
    };

    return this.httpClient.post<IItemProcess>(
      `${environment.apiUrl}/${ITEM_PROCESS_CATEGORIES_API_PREFIX}`,
      payload,
    );
  }

  /**
   * Updates the base price for a given category key.
   * @param categoryKey The key of the category to update.
   * @param basePrice The new base price.
   * @returns Observable<any> Observable indicating success or failure.
   */
  updateBasePriceForCategory(categoryKey: string, basePrice: number): Observable<any> {
    return this.httpClient.patch<any>(
      `${environment.apiUrl}/${ITEM_PROCESS_CATEGORIES_API_PREFIX}/categories/${categoryKey}`,
      { basePrice },
    );
  }

  private getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      FILLERS_MISC_LABOR: 'item-process-fillers-misc-labor',
      COPPER: 'item-process-copper',
      SINGLE_END: 'item-process-single-end',
      COMPOUNDS: 'item-process-compounds',
      TEFLON_TAPES: 'item-process-teflon-tapes',
      ALL_TAPES_AND_FILLERS: 'item-process-all-tapes-and-fillers',
    };

    return colorMap[category] || 'item-process-default';
  }

  // TODO: Use this for API call - get item processes
  // getItemProcesses(payload: SafeAny): Observable<SafeAny> {
  //   return this.httpClient.get<SafeAny>(`${environment.apiUrl}/item-processes`, {
  //     params: payload,
  //   });
  // }

  // TODO: Use this for API call - add item processes
  // addItemProcess(payload: SafeAny): Observable<SafeAny> {
  //   console.log('🚀 ~ addItemProcess:', payload);
  //   return this.httpClient.post<SafeAny>(`${environment.apiUrl}/item-processes`, payload);
  // }

  loadItemProcessCategories(): Observable<SafeAny> {
    return this.httpClient.get<SafeAny>(`${environment.apiUrl}/item-processes/categories`);
  }

  updateItemProcess(payload: Partial<IItemProcess>, itemId: string): Observable<IItemProcess> {
    return this.httpClient.put<IItemProcess>(
      `${environment.apiUrl}/item-processes/${itemId}`,
      payload,
    );
  }

  /**
   * Gets a single ItemProcessGroup based on category key and pagination parameters.
   */
  getSingleItemGroup(
    categoryKey: string,
    q: string | undefined,
    limit: number,
    offset: number,
    sort?: string,
  ): Observable<ItemProcessGroup | null> {
    const pageIndex = offset - 1;
    const sortActive = sort ? sort.replace(/^-/, '') : '';
    const sortDirection = sort ? (sort.startsWith('-') ? 'desc' : 'asc') : '';

    return this.getItemProcessCategories().pipe(
      switchMap((categories) => {
        const targetCategory = categories.find((cat) => cat.key === categoryKey);
        if (!targetCategory) {
          console.error(`Category not found for key: ${categoryKey}`);
          return of(null); // Category not found
        }

        // 2. Fetch items for the specific category
        return this.getItemProcessesForCategory(categoryKey, q, limit, offset, sort).pipe(
          map((response) =>
            this.mapCategoryResponseToGroup(
              targetCategory,
              response,
              limit,
              pageIndex,
              sortActive,
              sortDirection,
            ),
          ),
        );
      }),
    );
  }

  /**
   * Helper method to map category details and API response to an ItemProcessGroup.
   */
  private mapCategoryResponseToGroup(
    category: ItemProcessCategory,
    response: HttpResponse<IItemProcess[]>,
    limit: number,
    pageIndex: number,
    sortActive: string,
    sortDirection: SortDirection,
  ): ItemProcessGroup {
    return {
      name: category.value,
      categoryKey: category.key,
      color: this.getCategoryColor(category.key),
      hasBasePrice: category.basePrice !== null,
      basePrice: category.basePrice ?? undefined,
      items: response.body || [],
      pagination: {
        totalCount: Number(response.headers.get('X-Total-Count')) || 0,
        pageIndex: pageIndex,
        pageSize: limit,
      },
      sort: {
        active: sortActive,
        direction: sortDirection,
      },
    };
  }

  deleteItemProcess(itemId: number): Observable<void> {
    return this.httpClient.delete<void>(`${environment.apiUrl}/item-processes/${itemId}`);
  }
}
